import io from 'socket.io-client';
import { IPrintPluginInfo, PrintPluginStatus } from '@/modules/PrintService';
import { ShallowRef, ref, shallowRef } from 'vue';
import {
  IPrintingStrategy,
  IPrintRawBytesParams,
  usePrintingStore,
} from '../use-printing';
import { IPrinter } from '@/interfaces/IPrinter';
import { concatTypedArrays } from '@/utils';

/** Legacy strategy surface: exposes the plugin's `info` payload so the
 * strategy wiring can detect the new bridge's legacy emulation (protocol 2). */
export interface ILegacyPluginStrategy extends IPrintingStrategy {
  pluginInfo: Readonly<ShallowRef<IPrintPluginInfo | null>>;
}

export function isLegacyPluginStrategy(
  s: IPrintingStrategy
): s is ILegacyPluginStrategy {
  return 'pluginInfo' in s;
}

export function printingStrategyPlugin(): ILegacyPluginStrategy {
  const socket = shallowRef(null as SocketIOClient.Socket | null);
  const status = ref<PrintPluginStatus>(PrintPluginStatus.Disconnected);
  const pluginInfo = shallowRef(null as IPrintPluginInfo | null);
  let printingStore: ReturnType<typeof usePrintingStore>;

  function setStore(store: ReturnType<typeof usePrintingStore>) {
    printingStore = store;
  }

  function connect() {
    // An existing client reconnects forever on its own: creating another one
    // would stack immortal sockets with duplicate handlers. disconnect()
    // nulls the ref, so an explicit disconnect -> connect cycle still works.
    if (socket.value) return;

    status.value = PrintPluginStatus.Connecting;

    // Infinite reconnection: the plugin may start after the browser (e.g. PC
    // reboot). Loopback socket, so retrying forever is free.
    const s = (socket.value = io('ws://127.0.0.1:3030/', {
      secure: false,
      autoConnect: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    }));

    s.on('info', (data) => {
      console.log('info', data);
      pluginInfo.value = data;
      printingStore.setPluginVersion(data.version);
    });

    s.on('connect', (event) => {
      status.value = PrintPluginStatus.Connected;
    });

    s.on('reconnecting', (event) => {
      status.value = PrintPluginStatus.Connecting;
    });

    s.on('disconnect', (event) => {
      status.value = PrintPluginStatus.Disconnected;
    });

    s.on('error', (event) => {
      if (!isConnected()) status.value = PrintPluginStatus.Disconnected;
    });

    s.on('reconnect_failed', (event) => {
      status.value = PrintPluginStatus.Disconnected;
    });
  }

  function disconnect() {
    const s = socket.value;
    socket.value = null;
    if (s) {
      s.removeAllListeners();
      s.close();
    }
    status.value = PrintPluginStatus.Disconnected;
  }

  function isConnected() {
    return socket.value?.connected;
  }

  async function getPrinters(mode: 'usb' | 'bluetooth') {
    if (!isConnected()) throw new Error('El socket no está conectado');

    const s = socket.value;

    return new Promise<IPrinter[]>((resolve, reject) => {
      try {
        s.once('printers', (printers: any[]) => {
          resolve(
            printers.map((p) => ({
              id: p.name,
              name: p.name,
              type: mode,
            }))
          );
        });

        s.emit('printers');

        setTimeout(() => {
          s.off('printers');
          reject(new Error('Timeout'));
        }, 2000);
      } catch (error) {
        s.off('printers');
        reject(error);
      }
    });
  }

  async function printRawBytes(data: IPrintRawBytesParams) {
    if (!isConnected()) throw new Error('El socket no está conectado');

    const s = socket.value;

    if (!data.id) {
      if (!data.printer) {
        throw new Error(
          'If no printer id is provided, a printer object must be provided'
        );
      }

      data.id = data.printer.id;
      data.type = data.printer.type;
    }

    const encoder = new TextEncoder();
    const nameEncoded = encoder.encode(data.printer.name + '\0'); // Includes the null terminator
    const buf = new ArrayBuffer(nameEncoded.length + 4 + data.bytes.length);
    const view = new DataView(buf);

    // Copy encoded name
    let i = 0;
    nameEncoded.forEach((byte) => {
      view.setUint8(i++, byte);
    });

    // Set printer ID
    const uniqueId = parseInt(Math.random() * 1000000 + '', 10);
    view.setUint32(i, uniqueId, true); // Little endian
    i += 4;

    // Prepare final data
    const dataBeforeBytes = new Uint8Array(buf.slice(0, i));
    const final = concatTypedArrays(
      dataBeforeBytes,
      new Uint8Array(data.bytes)
    );

    s.emit('printv2', final.buffer);
  }

  return {
    status,
    pluginInfo,
    setStore,
    connect,
    disconnect,
    getPrinters,
    printRawBytes,
  };
}
